import { FetchReportRequest, FetchReportResponse } from 'src/models/reports/FetchReport';
import { TPTaxReportData, TPTaxReportMapResponse } from 'src/models/reports/TPTaxReport';
import { DETAIL } from 'src/utils/constants';

/**
 * Pure helpers behind LEF CLI-level provenance (Epic 3, Stories 3.1 + 3.2). No React, so they can be
 * unit-tested directly and reused by MAR / CWB later.
 *
 * Contract, confirmed against gamma LEF responses (TP_TAX_LEF_v1#9R#MAY-26#7 and #01#MAY-26#7):
 *
 *  - Functional Segment = "All" returns reconciliation + segment-total columns only:
 *        ["Difference", "OFA Balances", "Total CLIs (ATP)", "Total <segment>", ...]
 *    and NO per-CLI columns.
 *  - Functional Segment = <segment> returns ["Total <segment>", <cli>, <cli>, ...] with NO
 *    reconciliation columns, paginated by column (page size counts the Total column), with
 *    `lastPage` and the full set in `filteredHeaders`. It is the same stored report, filtered.
 *  - DETAIL carries each CLI's Functional Segment Category / Functional Segment / Second Party
 *    Oracle ID, keyed by CLI.
 *
 * So expanding a segment = keep the "All" rows as the base (they own the reconciliation columns),
 * fetch that segment, and merge its CLI columns into the base rows by attributeNameHash.
 */

// ---------------------------------------------------------------------------------------------------
// Header classification
// ---------------------------------------------------------------------------------------------------

/**
 * Entity-level reconciliation columns. They are pinned and never moved by expand/collapse (Story 3.2).
 * The backend names the first one "Difference"; the mockups' "Unallocated (Diff)" is a UI label only.
 */
export const RECONCILIATION_HEADERS: ReadonlyArray<string> = Object.freeze([
    'Difference',
    'OFA Balances',
    'Total CLIs (ATP)',
]);

const SEGMENT_TOTAL_PREFIX = 'Total ';

export function isReconciliationHeader(header: string): boolean {
    return RECONCILIATION_HEADERS.includes(header);
}

/** "Total <functional segment>" — every "Total " header that is not a reconciliation column. */
export function isSegmentTotalHeader(header: string): boolean {
    return header.startsWith(SEGMENT_TOTAL_PREFIX) && !isReconciliationHeader(header);
}

/**
 * The Functional Segment Category a "Total <segment>" header stands for — the value sent back as
 * `functionalSegmentCategory` to fetch that segment. When the report view's category list is known,
 * an exact match wins, then a case-insensitive one, so a casing drift between the two payloads cannot
 * send a category the backend does not recognise. Falls back to the header's own text.
 */
export function segmentCategoryFromHeader(header: string, knownCategories?: ReadonlyArray<string> | null): string {
    const derived = header.startsWith(SEGMENT_TOTAL_PREFIX) ? header.slice(SEGMENT_TOTAL_PREFIX.length).trim() : header;
    if (!knownCategories || knownCategories.length === 0) {
        return derived;
    }
    if (knownCategories.includes(derived)) {
        return derived;
    }
    const lower = derived.toLowerCase();
    return knownCategories.find((category) => category.toLowerCase() === lower) ?? derived;
}

/** Drill-in only makes sense when the base report is the "All" view: at least one segment-total column. */
export function hasDrillableSegments(headers: ReadonlyArray<string> | undefined | null): boolean {
    return (headers ?? []).some(isSegmentTotalHeader);
}

// ---------------------------------------------------------------------------------------------------
// Column model
// ---------------------------------------------------------------------------------------------------

export type ProvenanceColumn =
    | { kind: 'reconciliation'; id: string }
    | { kind: 'segmentTotal'; id: string; expanded: boolean }
    | { kind: 'cli'; id: string; segmentHeader: string }
    | { kind: 'other'; id: string };

/**
 * Column order for the TP P&L grid:
 *   1. reconciliation columns, in payload order (always first, so they can be sticky and never move);
 *   2. each segment total, immediately followed by its CLI columns when that segment is expanded;
 *   3. anything unrecognised, in payload order, so an unexpected header is shown rather than dropped.
 *
 * Collapsing a segment removes only its CLI columns; nothing else changes position.
 */
export function buildProvenanceColumns(
    baseHeaders: ReadonlyArray<string>,
    expandedClisBySegment: { [segmentHeader: string]: ReadonlyArray<string> }
): ProvenanceColumn[] {
    const reconciliation: ProvenanceColumn[] = [];
    const segments: ProvenanceColumn[] = [];
    const other: ProvenanceColumn[] = [];
    const seen = new Set<string>();

    baseHeaders.forEach((header) => {
        if (seen.has(header)) {
            return;
        }
        seen.add(header);
        if (isReconciliationHeader(header)) {
            reconciliation.push({ kind: 'reconciliation', id: header });
        } else if (isSegmentTotalHeader(header)) {
            const clis = expandedClisBySegment[header];
            segments.push({ kind: 'segmentTotal', id: header, expanded: !!clis });
            (clis ?? []).forEach((cli) => {
                if (!seen.has(cli)) {
                    seen.add(cli);
                    segments.push({ kind: 'cli', id: cli, segmentHeader: header });
                }
            });
        } else {
            other.push({ kind: 'other', id: header });
        }
    });

    return [...reconciliation, ...segments, ...other];
}

/** Attribute Name plus the leading reconciliation columns — the count to pin with stickyColumns.first. */
export function stickyColumnCount(columns: ReadonlyArray<ProvenanceColumn>): number {
    let count = 0;
    while (count < columns.length && columns[count].kind === 'reconciliation') {
        count++;
    }
    return 1 + count;
}

// ---------------------------------------------------------------------------------------------------
// Segment fetch + merge
// ---------------------------------------------------------------------------------------------------

export interface CliCaption {
    /** "Sub-lease → 2D", from DETAIL's Functional Segment and Second Party Oracle ID. */
    caption: string;
    currency?: string;
}

export interface SegmentCliData {
    /** The "Total <segment>" header this data belongs to. */
    segmentHeader: string;
    /** CLI columns in backend order, across every fetched page, de-duplicated. */
    clis: string[];
    /** Per reportContent key (TP_PNL, TP_PNL_Cost_Center, ...): attributeNameHash -> {cli: value}. */
    valuesByContentKey: { [contentKey: string]: { [attributeNameHash: string]: { [cli: string]: string } } };
    captions: { [cli: string]: CliCaption };
    /** True when the backend reported more pages than MAX_SEGMENT_PAGES allowed us to read. */
    truncated: boolean;
}

/**
 * Columns per request. 15 is the page size the LEF view already uses, so it is known to be accepted;
 * it includes the Total column, so each page carries 14 CLIs. Pages after the first are fetched in
 * parallel.
 */
export const SEGMENT_FETCH_PAGE_SIZE = 15;
/** Safety cap (~140 CLIs) so a malformed lastPage can never fan out without bound. */
export const MAX_SEGMENT_PAGES = 10;

interface ParsedSegmentPage {
    reportData: TPTaxReportMapResponse;
    lastPage: number;
}

/** Parses one FetchReportData response; throws with the backend's message when there is no payload. */
export function parseSegmentPage(response: FetchReportResponse | null | undefined): ParsedSegmentPage {
    if (!response?.payload) {
        throw new Error(response?.statusMessage || 'The segment report returned no data.');
    }
    const payload = JSON.parse(response.payload);
    const reportData = payload?.tpTaxReportJSONData as TPTaxReportMapResponse | undefined;
    if (!reportData || !Array.isArray(reportData.reportHeaders)) {
        throw new Error('The segment report returned no data.');
    }
    const lastPage = Number(payload?.lastPage);
    return { reportData, lastPage: Number.isFinite(lastPage) && lastPage > 0 ? lastPage : 1 };
}

/** CLI columns of a segment page: everything except the Total and any reconciliation column. */
export function cliHeadersOf(headers: ReadonlyArray<string>): string[] {
    return headers.filter((header) => !isSegmentTotalHeader(header) && !isReconciliationHeader(header));
}

function detailValue(detailRows: TPTaxReportData[] | undefined, displayName: string, cli: string): string | undefined {
    const value = detailRows?.find((row) => row.displayName === displayName)?.attributeValue?.[cli];
    const trimmed = value === undefined || value === null ? '' : `${value}`.trim();
    // "N/A" / "Not Applicable" are placeholders, not captions.
    return trimmed && trimmed !== 'N/A' && trimmed !== 'Not Applicable' ? trimmed : undefined;
}

/** "Sub-lease → 2D"; either half may be missing (e.g. CLI ids ending in "-" carry no counterparty). */
export function buildCliCaption(detailRows: TPTaxReportData[] | undefined, cli: string): CliCaption {
    const segment =
        detailValue(detailRows, 'Functional Segment', cli) ?? detailValue(detailRows, 'Functional Segment Category', cli);
    const counterparty = detailValue(detailRows, 'Second Party Oracle ID', cli);
    const caption = [segment, counterparty].filter(Boolean).join(' → ');
    return { caption, currency: detailValue(detailRows, 'FRAMES Currency', cli) };
}

/** Merges already-parsed segment pages into one SegmentCliData. Content keys other than DETAIL are kept. */
export function mergeSegmentPages(segmentHeader: string, pages: ReadonlyArray<TPTaxReportMapResponse>): SegmentCliData {
    const clis: string[] = [];
    const seen = new Set<string>();
    const valuesByContentKey: SegmentCliData['valuesByContentKey'] = {};
    const captions: SegmentCliData['captions'] = {};

    pages.forEach((page) => {
        const pageClis = cliHeadersOf(page.reportHeaders ?? []);
        pageClis.forEach((cli) => {
            if (!seen.has(cli)) {
                seen.add(cli);
                clis.push(cli);
            }
            captions[cli] = buildCliCaption(page.reportContent?.[DETAIL], cli);
        });

        Object.entries(page.reportContent ?? {}).forEach(([contentKey, rows]) => {
            if (contentKey === DETAIL || !Array.isArray(rows)) {
                return;
            }
            const byHash = (valuesByContentKey[contentKey] = valuesByContentKey[contentKey] ?? {});
            rows.forEach((row) => {
                const values = row.attributeValue ?? {};
                pageClis.forEach((cli) => {
                    if (values[cli] !== undefined) {
                        (byHash[row.attributeNameHash] = byHash[row.attributeNameHash] ?? {})[cli] = values[cli];
                    }
                });
            });
        });
    });

    return { segmentHeader, clis, valuesByContentKey, captions, truncated: false };
}

/**
 * Fetches every page of one functional segment and merges it. `buildRequest(page, pageSize)` must
 * return the base report's request with functionalSegmentCategory set to the segment.
 */
export async function fetchSegmentCliData(
    segmentHeader: string,
    buildRequest: (pageNumber: number, pageSize: number) => FetchReportRequest,
    fetchPage: (request: FetchReportRequest) => Promise<FetchReportResponse>
): Promise<SegmentCliData> {
    const first = parseSegmentPage(await fetchPage(buildRequest(1, SEGMENT_FETCH_PAGE_SIZE)));
    const pageCount = Math.min(first.lastPage, MAX_SEGMENT_PAGES);
    const rest = await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, index) =>
            fetchPage(buildRequest(index + 2, SEGMENT_FETCH_PAGE_SIZE)).then((response) => parseSegmentPage(response).reportData)
        )
    );
    const merged = mergeSegmentPages(segmentHeader, [first.reportData, ...rest]);
    return { ...merged, truncated: first.lastPage > MAX_SEGMENT_PAGES };
}

/**
 * Base rows with every expanded segment's CLI values folded into attributeValue. Cells for a row the
 * segment response did not carry stay absent (blank), never zero. Returns the input unchanged when
 * nothing is expanded, so the collapsed view is referentially identical to the flag-off view.
 */
export function mergeCliValuesIntoRows(
    rows: TPTaxReportData[],
    contentKey: string,
    expandedSegments: ReadonlyArray<SegmentCliData>
): TPTaxReportData[] {
    const perSegment = expandedSegments.map((segment) => segment.valuesByContentKey[contentKey]).filter(Boolean);
    if (perSegment.length === 0) {
        return rows;
    }
    return rows.map((row) => {
        let extra: { [cli: string]: string } | undefined;
        perSegment.forEach((byHash) => {
            const values = byHash[row.attributeNameHash];
            if (values) {
                extra = { ...(extra ?? {}), ...values };
            }
        });
        return extra ? { ...row, attributeValue: { ...extra, ...row.attributeValue } } : row;
    });
}
